import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionMessage } from '../../types/protocol';

interface GroupFieldProps {
  field: string;
  children: QuestionMessage[];
  help?: string;
  onSubmit: (value: unknown) => void;
}

export function GroupField({ field: _field, children, help, onSubmit }: GroupFieldProps): React.ReactElement {
  const { t } = useTranslation();
  // Initialize values from child defaults
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const child of children) {
      // Use the last segment of the dotted field name as key
      const key = child.field.includes('.') ? child.field.split('.').pop()! : child.field;
      init[key] = child.default ?? (child.fieldType === 'boolean' ? false : child.fieldType === 'number' ? 0 : '');
    }
    return init;
  });

  const setValue = useCallback((childField: string, value: unknown) => {
    const key = childField.includes('.') ? childField.split('.').pop()! : childField;
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(values);
    },
    [values, onSubmit],
  );

  return (
    <form className="sp-field sp-field-group" onSubmit={handleSubmit}>
      <div className="sp-group-fields">
        {children.map((child) => {
          const key = child.field.includes('.') ? child.field.split('.').pop()! : child.field;
          const value = values[key];

          // Client-side visibleWhen: check intra-group conditions
          if (child.visibleWhen) {
            const visible = Object.entries(child.visibleWhen).every(([field, expected]) => {
              const refKey = field.includes('.') ? field.split('.').pop()! : field;
              return String(values[refKey] ?? '') === String(expected);
            });
            if (!visible) return null;
          }

          if (child.fieldType === 'boolean') {
            const checked = !!value;
            return (
              <label key={child.field} className="sp-group-toggle-row">
                <span className="sp-group-field-label">{child.label}</span>
                <button
                  type="button"
                  className={`sp-toggle${checked ? ' sp-toggle-on' : ''}`}
                  role="switch"
                  aria-checked={checked}
                  onClick={() => setValue(child.field, !checked)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault();
                      setValue(child.field, !checked);
                    }
                  }}
                >
                  <span className="sp-toggle-track" />
                  <span className="sp-toggle-thumb" />
                </button>
              </label>
            );
          }

          if (child.fieldType === 'number') {
            return (
              <label key={child.field} className="sp-group-input-row">
                <span className="sp-group-field-label">{child.label}</span>
                <input
                  type="number"
                  className="sp-field-input sp-group-number-input"
                  value={value as number}
                  onChange={(e) => setValue(child.field, e.target.value === '' ? '' : Number(e.target.value))}
                />
              </label>
            );
          }

          if (child.fieldType === 'select_one' && child.options) {
            return (
              <label key={child.field} className="sp-group-input-row">
                <span className="sp-group-field-label">{child.label}</span>
                <select
                  className="sp-field-input sp-group-select"
                  value={String(value ?? '')}
                  onChange={(e) => setValue(child.field, e.target.value)}
                >
                  {child.options.map((opt) => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          }

          if (child.fieldType === 'text') {
            return (
              <label key={child.field} className="sp-group-input-row">
                <span className="sp-group-field-label">{child.label}</span>
                <input
                  type="text"
                  className="sp-field-input sp-group-text-input"
                  value={String(value ?? '')}
                  onChange={(e) => setValue(child.field, e.target.value)}
                  placeholder={child.help}
                />
              </label>
            );
          }

          return null;
        })}
      </div>
      {help && <span className="sp-field-help">{help}</span>}
      <button className="sp-field-submit" type="submit">
        {t('fields.submit')}
      </button>
    </form>
  );
}
